import request from 'yeself-common/utils/RequestUtils';
import {
    ApplicationAccessData,
    ApplicationId,
    ApplicationIdentityDefinition,
    ApplicationTrustedDomainValue,
    ApplicationThemeDefinition,
} from 'yeself-common/typings/application/Application';

import EnvironmentConfiguration from 'yeself-client-common/EnvironmentConfiguration';

import { ExtensionUrl } from './types';

export const fetchLanguageList = (appId) =>
    request.get(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/availableLocales`
    );

export const updateApplicationDetail = (appId, settings) =>
    request.put(`${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}`, settings);

export const getRolloutPoliciesByMyOrganizations = (): ReturnType<typeof request.get> =>
    request.get(`${EnvironmentConfiguration.API_MANAGE_SLUG}/rollout-policy/get-by-my-organizations`);

export const getRolloutPoliciesBySpaceId = (spaceId: ApplicationId): ReturnType<typeof request.get> =>
    request.get(`${EnvironmentConfiguration.API_MANAGE_SLUG}/rollout-policy/get-by-space-id/${spaceId}`);

export const updateApplicationAccess = (
    applicationId: ApplicationId,
    accessAcl: ApplicationAccessData
): ReturnType<typeof request.put> =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/access`,
        accessAcl
    );

export const updateApplicationIdentity = (
    applicationId: ApplicationId,
    identity: ApplicationIdentityDefinition
): ReturnType<typeof request.put> =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/identity`,
        identity
    );

export const updateApplicationTheme = (
    applicationId: ApplicationId,
    theme: ApplicationThemeDefinition
): ReturnType<typeof request.put> =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/theme`,
        theme
    );

export const updateApplicationOriginUrls = (
    applicationId: ApplicationId,
    extensionUrls: ExtensionUrl[]
): ReturnType<typeof request.put> =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/origin-urls`,
        extensionUrls
    );

export const updateApplicationTrustedDomains = (
    applicationId: ApplicationId,
    trustedDomains: ApplicationTrustedDomainValue[]
): ReturnType<typeof request.put> =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${applicationId}/trusted-domains`,
        trustedDomains
    );

export const fetchAppUsersListService = (organizationId) =>
    request.get(
        `${EnvironmentConfiguration.API_IDENTITY_SLUG}/manage/accounts?organizationId=${organizationId}`
    );

export const inviteUser = (appId, payload) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/access/emails`,
        payload
    );

const exportTranslationsRequestAcceptHeaderValue = (format) => {
    switch (format) {
        case 'excel':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'csv':
        default:
            return 'application/zip';
    }
};

export const exportTranslationsRequest = (appId, format) =>
    request.get(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/translations`,
        {
            responseType: 'blob',
            header: {
                Accept: exportTranslationsRequestAcceptHeaderValue(format),
            },
        }
    );

export const exportAddOnTranslationsRequest = (url) =>
    request.get(url, {
        responseType: 'blob',
        header: {
            Accept: 'application/zip',
        },
    });

export const importTranslationsRequest = (appId, file) =>
    request.upload(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/translations`,
        { name: file.name, file }
    );

export const importAddOnTranslationsRequest = (url, file) =>
    request.upload(
        url,
        { name: file.name, file },
        { header: { 'Content-Type': 'multipart/form-data' } }
    );

export const fetchAddOnAvailableListRequest = (appId) =>
    request.get(
        `${EnvironmentConfiguration.ADDONS_SLUG}/manage/applications/${appId}/addons/view/available`
    );

export const fetchInstalledListAddOnsRequest = (appId) =>
    request.get(
        `${EnvironmentConfiguration.ADDONS_SLUG}/manage/applications/${appId}/addons/view/installed`
    );

export const fetchAddOnOauthAuthorizationCodeUrlRequest = (
    appId,
    addonSystemKey,
    addonInstanceSystemKey
) =>
    request.get(
        `${EnvironmentConfiguration.ADDONS_SLUG}/manage/applications/${appId}/addons/${addonSystemKey}/instances/${addonInstanceSystemKey}/oauth/authorization-code`
    );

export const loadArticlesPickerScripRequest = (appId) =>
    request.get(
        `${EnvironmentConfiguration.ADDONS_SLUG}/manage/applications/${appId}/addons/article-config`
    );

export const deleteAddOnsInstalledListRequest = (appId, addonKey, addonInstanceSystemKey) =>
    request.del(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/addons/${addonKey}/instances/${addonInstanceSystemKey}`
    );

export const createAddOnRequest = (appId, addonKey, payload) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/addons/${addonKey}`,
        payload
    );

export const updateAddOnRequest = (appId, addonKey, addonInstanceSystemKey, payload) =>
    request.put(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/addons/${addonKey}/instances/${addonInstanceSystemKey}`,
        payload
    );

export const getVaultPublicKeyRequest = (appId) =>
    request.get(
        `https://yeself-cloud-int-app.brux.space/kong/yeself-vault/v1/transit/keys/transit-${appId}`,
        { header: { 'X-Vault-Token': 'myroot' } }
    );

export const deleteFactsRequest = (appId) =>
    request.del(`${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/facts`);

export const deleteFactsForUserRequest = (appId, userId) =>
    request.del(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/facts/user/${userId}`
    );

export const generateGuideRequest = (payload) => {
    const header = { 'Content-Type': 'application/json' };
    request.post(`${EnvironmentConfiguration.API_ORCHESTRATOR_SLUG}/manage/pipelines`, payload, {
        header,
    });
};

export const duplicateApplicationRequest = (appId, payload) =>
    request.post(
        `${EnvironmentConfiguration.API_MANAGE_SLUG}/manage/applications/${appId}/commands/clone`,
        payload
    );
